import { useMemo } from "react";
import { usePermissionsMap, type PermissionsMap } from "@/hooks/usePermissionsMap";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useClinicsForUser } from "@/hooks/useClinics";
import { useProfile } from "@/hooks/useProfile";
import type {
  NavigationConfig,
  NavigationSection,
  NavigationItem,
} from "@/lib/types/navigation";
import { NAVIGATION_CONFIG } from "@/lib/navigation-config";

/**
 * Result from useFilteredNavigation hook
 */
interface FilteredNavigationResult {
  /** Filtered navigation sections visible to the current user */
  sections: NavigationSection[];
  /** True while permissions or entitlements are loading */
  isLoading: boolean;
}

/**
 * Hook that filters navigation based on:
 * 1. Active modules (from customer_modules table)
 * 2. Feature permissions (from get_user_all_permissions RPC)
 * 3. Custom visibility logic
 *
 * This hook replaces the hardcoded navigation filtering logic in AppLayout.tsx
 * and provides a single source of truth for navigation visibility rules.
 *
 * Performance: Uses single RPC call via usePermissionsMap() - no N+1 queries
 *
 * @returns Filtered navigation sections and loading state
 *
 * @example
 * ```tsx
 * function Sidebar() {
 *   const { sections, isLoading } = useFilteredNavigation();
 *
 *   if (isLoading) return <LoadingSkeleton />;
 *
 *   return sections.map(section => (
 *     <Section key={section.title}>
 *       {section.items.map(item => <NavItem {...item} />)}
 *     </Section>
 *   ));
 * }
 * ```
 */
export function useFilteredNavigation(): FilteredNavigationResult {
  const { data: profile, isLoading: isLoadingProfile, isError: isProfileError } = useProfile();
  const { data: permissionsMap, isLoading: isLoadingPermissions } = usePermissionsMap();
  const { data: entitlements, isLoading: isLoadingEntitlements } = useEntitlements();
  const { data: clinics = [], isLoading: isLoadingClinics } = useClinicsForUser();

  // Consider navigation loading until profile is loaded and has a clinic (so permissions/entitlements can run).
  // Treat profile error as pending so first-login (profile not yet created) shows loading until ensureCustomerAndClinic refetches.
  const isProfileOrClinicPending =
    isLoadingProfile ||
    isProfileError ||
    (profile !== undefined && !profile?.current_clinic_id);

  const sections = useMemo(() => {
    // Wait for all data to load
    if (!permissionsMap || !entitlements) {
      return [];
    }

    const activeModules = entitlements.activeModules;
    const clinicsCount = clinics.length;

    return NAVIGATION_CONFIG.map((section) => {
      // Filter section by module
      // If section has moduleKey and that module is not active, hide entire section
      if (section.moduleKey && !activeModules.includes(section.moduleKey)) {
        return null;
      }
      if (section.moduleKeysAny && !section.moduleKeysAny.some((m) => activeModules.includes(m))) {
        return null;
      }

      // Filter items within section based on permissions
      const visibleItems = section.items.filter((item) => {
        return isNavigationItemVisible(item, permissionsMap, clinicsCount);
      });

      // Hide section if no visible items
      if (visibleItems.length === 0) {
        return null;
      }

      return {
        ...section,
        items: visibleItems,
      };
    })
      .filter((section): section is NavigationSection => section !== null);
  }, [permissionsMap, entitlements, clinics]);

  return {
    sections,
    isLoading:
      isProfileOrClinicPending ||
      isLoadingPermissions ||
      isLoadingEntitlements ||
      isLoadingClinics,
  };
}

/**
 * Check if single navigation item should be visible to current user
 *
 * Visibility hierarchy:
 * 1. alwaysVisible → show without permission check (e.g., Home)
 * 2. customVisibilityCheck → use custom function (e.g., Settings)
 * 3. alternativeFeatures → show if ANY feature grants permission (OR logic)
 * 4. featureKey → show if user has required permission for feature
 * 5. default → hide if no access control defined
 *
 * @param item - Navigation item to check
 * @param permissionsMap - Current user's permissions map from database
 * @param clinicsCount - Number of clinics user has access to
 * @returns true if item should be visible
 */
function isNavigationItemVisible(
  item: NavigationItem,
  permissionsMap: PermissionsMap,
  clinicsCount: number
): boolean {
  // 1. Always visible items (e.g., Home)
  if (item.alwaysVisible) {
    return true;
  }

  // 2. Custom visibility check (e.g., Settings)
  if (item.customVisibilityCheck) {
    return item.customVisibilityCheck(permissionsMap, clinicsCount);
  }

  // 3. Alternative features (OR logic)
  // Show if user has permission for ANY of the alternative features
  // Example: "Clientes" visible if user has owners:view OR pets:view
  if (item.alternativeFeatures && item.alternativeFeatures.length > 0) {
    return item.alternativeFeatures.some((alt) => {
      const feature = permissionsMap[alt.featureKey];
      return feature?.[alt.requiredAction] === true;
    });
  }

  // 4. Single feature check
  // Show if user has required permission for the feature
  if (item.featureKey) {
    const feature = permissionsMap[item.featureKey];
    const action = item.requiredAction || "view";
    return feature?.[action] === true;
  }

  // Default: hide if no access control defined
  // This ensures new items without proper configuration are hidden by default
  return false;
}
